import React from 'react';
import EmptyState from '../shared/EmptyState';
import LoadingSpinner from '../shared/LoadingSpinner';
import UserRow from './UserRow';
import PaginationControls from './PaginationControls';

/**
 * UsersTable - Table component for displaying users directory
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isLoading - Whether data is loading
 * @param {Array} props.users - Array of user objects to display
 * @param {Array} props.filteredUsers - Filtered users for count display
 * @param {Array} props.paginatedUsers - Paginated users to display in table
 * @param {number} props.currentPage - Current page number
 * @param {number} props.totalPages - Total number of pages
 * @param {number} props.itemsPerPage - Items per page for pagination
 * @param {string|null} props.processingAction - ID of user currently being processed
 * @param {Function} props.onToggleAdmin - Called when admin checkbox is toggled
 * @param {Function} props.onDeleteUser - Called when delete button is clicked
 * @param {Function} props.onPageChange - Called when page changes
 * @returns {React.ReactElement} Users table component
 */
const UsersTable = ({
  isLoading,
  users,
  filteredUsers,
  paginatedUsers,
  currentPage,
  totalPages,
  itemsPerPage,
  processingAction,
  onToggleAdmin,
  onDeleteUser,
  onPageChange,
  selectedUserId,
  onSelectUser
}) => {
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (filteredUsers.length === 0) {
    return (
      <EmptyState
        iconClass="fa-solid fa-users-slash"
        iconSize="2.5rem"
        message="No users found matching your search scope."
        variant="card"
      />
    );
  }

  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
              <th style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Full Name</th>
              <th style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Email Address</th>
              <th style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Title & Company</th>
              <th style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Mapped Organizations</th>
              <th style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', textAlign: 'center' }}>Super Admin</th>
              <th style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.map((u, idx) => (
              <UserRow
                key={u.id}
                user={u}
                index={idx}
                totalRows={paginatedUsers.length}
                processingAction={processingAction}
                onToggleAdmin={onToggleAdmin}
                onDeleteUser={onDeleteUser}
                selected={selectedUserId === u.id}
                onSelect={onSelectUser}
              />
            ))}
          </tbody>
        </table>
      </div>
      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={filteredUsers.length}
        itemsPerPage={itemsPerPage}
        itemType="users"
        onPrevious={() => onPageChange(currentPage - 1)}
        onNext={() => onPageChange(currentPage + 1)}
      />
    </>
  );
};

export default UsersTable;